import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { mainBus, metricsBus } from "@/common/buses";
import { routes } from "@/common/utils/routes";
import { useSendEvent } from "@/components/search/GlobalSearch/hooks/useSendEvent";
import { useAppStore } from "@/common/store/appStore";
import { apiSearch } from "@/common/services";
import { isAuthenticated } from "@/common/services/Auth";

interface IUseSearchInputHandlers {
  searchValue: string;
  onSetSearchValue: (value: string) => void;
}

export const useSearchInputHandlers = ({ searchValue, onSetSearchValue }: IUseSearchInputHandlers) => {
  const isSearchBarOpen = useAppStore((state) => state.isSearchBarOpen);
  const [isFocused, setFocused] = useState<boolean>(false);
  const toggleSearchBar = useAppStore((state) => state.toggleSearchBar);
  const { sendQuerySubmitted } = useSendEvent();

  const navigate = useNavigate();

  const handleFocus = useCallback(() => setFocused(true), []);
  const handleBlur = useCallback(() => setFocused(false), []);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onSetSearchValue(event.target.value);
  }, [onSetSearchValue]);

  const handleSelect = useCallback(() => {
    if (!isSearchBarOpen) {
      toggleSearchBar();
      metricsBus.emit("searchBarOpened");
    }
  }, [isSearchBarOpen]);

  const handleClear = useCallback(() => {
    onSetSearchValue("");
  }, [onSetSearchValue]);

  const handleCloseSearch = useCallback(() => {
    mainBus.emit("close_search");
  }, []);

  const addSearchHistoryItem = async (value: string, type: string) => {
    if (!(await isAuthenticated())) {
      return;
    }

    try {
      await apiSearch.addHistorySearchItem({ value, type });
    } catch (err) {
      console.error(err);
    }
  };

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      handleCloseSearch();
    }

    if (event.key === "Enter" && searchValue.length) {
      navigate(routes.search(searchValue));
      handleCloseSearch();
      sendQuerySubmitted([searchValue], "Manual");
      addSearchHistoryItem(searchValue, "free_type");
    }
  }, [searchValue]);

  return {
    isFocused,
    handleFocus,
    handleBlur,
    handleChange,
    handleSelect,
    handleClear,
    handleKeyDown,
    handleCloseSearch,
  };
};
